import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { questionsApi, runsApi, summariesApi, positionsApi } from '../utils/api'
import { useAppStore } from '../stores/appStore'
import {
  formatProbability,
  formatEdge,
  formatEdgeBps,
  formatTimeAgo,
  formatDateTime,
  formatPnL,
  formatQuantity,
  getEdgeClass,
  getAgentIcon,
  formatTimeCountdown,
  getTimeCountdownClass,
  formatSpread,
  getSpreadClass,
  calculateOpportunityScore,
  getOpportunityScoreClass,
  getActionBadgeClass,
  getProbabilityZone,
  getZoneClass,
  getZoneBadgeClass,
  getModeLabel as _getModeLabel,
} from '../utils/format'
import { RunTimeline } from './RunTimeline'
import { SummaryCard } from './SummaryCard'
import { PriceChart } from './PriceChart'
import clsx from 'clsx'
import { useState } from 'react'

export function QuestionDetail() {
  const { questionId } = useParams<{ questionId: string }>()
  const qid = parseInt(questionId || '0', 10)
  const queryClient = useQueryClient()
  const { openRunner } = useAppStore()
  
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null)
  const [showSummary, setShowSummary] = useState(false)
  const [paperTradeEnabled, setPaperTradeEnabled] = useState(true)
  
  // Fetch question detail
  const { data: question, isLoading, error } = useQuery({
    queryKey: ['question', qid],
    queryFn: () => questionsApi.get(qid),
    enabled: qid > 0,
  })
  
  // Fetch selected run detail
  const { data: runDetail } = useQuery({
    queryKey: ['run', selectedRunId],
    queryFn: () => runsApi.get(selectedRunId!),
    enabled: selectedRunId !== null,
  })
  
  // Fetch summary for selected run
  const { data: summary } = useQuery({
    queryKey: ['summary', selectedRunId],
    queryFn: () => summariesApi.get(selectedRunId!),
    enabled: selectedRunId !== null && showSummary,
    retry: false,
  })
  
  // Generate summary mutation
  const generateSummary = useMutation({
    mutationFn: (runId: number) => summariesApi.generate(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['summary', selectedRunId] })
    },
  })
  
  // Run position review mutation
  const runReview = useMutation({
    mutationFn: () => positionsApi.runReview(qid, 'manual'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['question', qid] })
    },
  })
  
  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 text-center">
        <div className="w-12 h-12 rounded-full border-2 border-signal-blue/30 border-t-signal-blue animate-spin mx-auto mb-4"></div>
        <p className="text-star-muted font-mono">Loading question...</p>
      </div>
    )
  }
  
  if (error || !question) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 text-center">
        <p className="text-signal-red font-mono mb-4">Failed to load question</p>
        <Link to="/questions" className="btn-secondary">← Back to Questions</Link>
      </div>
    )
  }
  
  const prob = question.consensus?.post_market_prob
  const marketProb = question.market?.prob
  const edge = prob !== undefined && marketProb !== undefined ? prob - marketProb : undefined
  
  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
      {/* Breadcrumb */}
      <div className="mb-4 sm:mb-6">
        <Link to="/questions" className="text-star-muted hover:text-star font-mono text-xs sm:text-sm">
          ← Back to Questions
        </Link>
      </div>

      {/* Resolution Banner */}
      {question.resolution && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={clsx(
            'mb-4 sm:mb-6 p-4 rounded-lg border-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2',
            question.resolution.toLowerCase().includes('yes') || question.resolution === '1'
              ? 'bg-signal-green/10 border-signal-green/50'
              : question.resolution.toLowerCase().includes('no') || question.resolution === '0'
                ? 'bg-signal-red/10 border-signal-red/50'
                : 'bg-signal-amber/10 border-signal-amber/50'
          )}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">
              {question.resolution.toLowerCase().includes('yes') || question.resolution === '1' ? '✅' :
               question.resolution.toLowerCase().includes('no') || question.resolution === '0' ? '❌' : '📋'}
            </span>
            <div>
              <span className="text-lg font-semibold text-star">
                RESOLVED: {question.resolution.toUpperCase()}
              </span>
              {question.consensus?.post_market_prob !== undefined && (
                <div className="text-xs text-star-muted font-mono mt-0.5">
                  Our prediction: {formatProbability(question.consensus.post_market_prob)}
                  {(() => {
                    const outcome = question.resolution.toLowerCase().includes('yes') || question.resolution === '1' ? 1 : 0
                    const wasCorrect = (question.consensus.post_market_prob >= 0.5 && outcome === 1) ||
                                      (question.consensus.post_market_prob < 0.5 && outcome === 0)
                    return (
                      <span className={clsx('ml-2', wasCorrect ? 'text-signal-green' : 'text-signal-red')}>
                        ({wasCorrect ? 'Correct' : 'Incorrect'})
                      </span>
                    )
                  })()}
                </div>
              )}
            </div>
          </div>
          <div className="text-xs text-star-dim font-mono">
            Market closed
          </div>
        </motion.div>
      )}

      {/* Header */}
      <div className="glass-card p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-2xl font-display font-semibold text-star mb-2 break-words">
              {question.title}
            </h1>
            {question.description && (
              <p className="text-star-muted text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-4">
                {question.description}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-[10px] sm:text-xs font-mono text-star-muted">
              {question.category && <span className="badge-muted text-[10px] sm:text-xs">{question.category}</span>}
              {question.status && <span className="badge-muted text-[10px] sm:text-xs">{question.status}</span>}
              {question.close_ts && (
                <span className="truncate">Closes: {formatDateTime(question.close_ts)}</span>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Paper Trade Toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <div
                className={clsx(
                  'relative w-10 h-5 rounded-full transition-colors',
                  paperTradeEnabled ? 'bg-signal-green/30' : 'bg-void-200'
                )}
                onClick={() => setPaperTradeEnabled(!paperTradeEnabled)}
              >
                <div
                  className={clsx(
                    'absolute top-0.5 w-4 h-4 rounded-full transition-all',
                    paperTradeEnabled
                      ? 'left-5 bg-signal-green'
                      : 'left-0.5 bg-star-dim'
                  )}
                />
              </div>
              <span className="text-[10px] sm:text-xs text-star-muted font-mono hidden xs:inline">Paper Trade</span>
            </label>

            <button
              className="btn-primary text-xs sm:text-sm"
              onClick={() => openRunner(question.id, question.title, paperTradeEnabled)}
            >
              ▶ <span className="hidden xs:inline">Run </span>Prediction
            </button>
          </div>
        </div>
        
        {/* Main probability display */}
        {question.analyzed_outcome?.name ? (
          /* Event with analyzed outcome - show what was predicted */
          <div className="mt-6 pt-6 border-t border-star-dim/20">
            <div className="mb-4">
              <span className="data-label">Predicted Most Likely Outcome</span>
              <div className="text-lg text-signal-green font-medium mt-1">
                {question.analyzed_outcome.name}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-6">
              <div>
                <span className="data-label text-[9px] sm:text-xs">Probability</span>
                <div className="data-value-lg text-xl sm:text-3xl text-signal-green">
                  {formatProbability(question.analyzed_outcome.probability)}
                </div>
                {question.consensus?.run_id && (
                  <span className="text-[10px] sm:text-xs text-star-dim font-mono">Run #{question.consensus.run_id}</span>
                )}
              </div>
              <div>
                <span className="data-label text-[9px] sm:text-xs">Confidence</span>
                <div className="data-value text-lg sm:text-2xl font-semibold text-signal-blue">
                  {question.analyzed_outcome.confidence
                    ? `${(question.analyzed_outcome.confidence * 100).toFixed(0)}%`
                    : '—'}
                </div>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <span className="data-label text-[9px] sm:text-xs">Child Markets</span>
                <div className="data-value text-lg sm:text-2xl">{question.child_markets.length}</div>
                <span className="text-[10px] sm:text-xs text-star-dim font-mono">total outcomes</span>
              </div>
            </div>
            {question.analyzed_outcome.summary && (
              <div className="mt-4 p-3 bg-void-50 rounded-lg">
                <span className="data-label">Analysis</span>
                <p className="text-sm text-star-muted mt-1 leading-relaxed">
                  {question.analyzed_outcome.summary}
                </p>
              </div>
            )}
          </div>
        ) : (
          /* Regular question or event without analyzed outcome */
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-6 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-star-dim/20">
            <div>
              <span className="data-label text-[9px] sm:text-xs">Our Prediction</span>
              <div className="data-value-lg text-xl sm:text-3xl">{formatProbability(prob)}</div>
              {question.consensus?.run_id && (
                <span className="text-[10px] sm:text-xs text-star-dim font-mono">Run #{question.consensus.run_id}</span>
              )}
            </div>
            {question.question_role === 'event' && question.child_markets.length > 0 ? (
              <>
                <div>
                  <span className="data-label text-[9px] sm:text-xs">Child Markets</span>
                  <div className="data-value text-lg sm:text-2xl">{question.child_markets.length}</div>
                  <span className="text-[10px] sm:text-xs text-star-dim font-mono">individual outcomes</span>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <span className="data-label text-[9px] sm:text-xs">Confidence</span>
                  <div className="data-value text-lg sm:text-2xl font-semibold text-signal-blue">
                    {question.consensus?.confidence
                      ? `${(question.consensus.confidence * 100).toFixed(0)}%`
                      : '—'}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <span className="data-label text-[9px] sm:text-xs">Market Price</span>
                  <div className="data-value text-lg sm:text-2xl">{formatProbability(marketProb)}</div>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <span className="data-label text-[9px] sm:text-xs">Edge</span>
                  <div className={clsx('data-value text-lg sm:text-2xl font-semibold', getEdgeClass(edge))}>
                    {formatEdge(edge)}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Decision Summary Card - At-a-glance decision context */}
      <DecisionSummaryCard
        prob={prob}
        edge={edge}
        spread={question.market?.spread_bps}
        liquidity={question.liquidity}
        confidence={question.consensus?.confidence}
        closeTs={question.close_ts}
        position={question.position}
        hasRuns={question.runs.length > 0}
      />

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Runs and chart */}
        <div className="lg:col-span-2 space-y-6">
          {/* Price chart */}
          <div className="glass-card p-4">
            <h3 className="font-mono text-sm text-star-muted mb-4">Price History</h3>
            <PriceChart 
              marketHistory={question.market_history}
              runs={question.runs}
            />
          </div>
          
          {/* Run timeline */}
          <div className="glass-card p-4">
            <h3 className="font-mono text-sm text-star-muted mb-4">
              Prediction Runs ({question.runs.length})
            </h3>
            <RunTimeline
              runs={question.runs}
              selectedRunId={selectedRunId}
              onSelectRun={setSelectedRunId}
            />
          </div>
          
          {/* Selected run detail */}
          <AnimatePresence mode="wait">
            {runDetail && (
              <motion.div
                key={runDetail.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="glass-card p-4"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-mono text-sm text-star-muted">
                    Run #{runDetail.id} Details
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      className={clsx(
                        'btn-secondary text-xs',
                        showSummary && 'bg-signal-blue/10 border-signal-blue/30 text-signal-blue'
                      )}
                      onClick={() => setShowSummary(!showSummary)}
                    >
                      📝 {showSummary ? 'Hide' : 'Show'} Summary
                    </button>
                    {showSummary && !summary && (
                      <button
                        className="btn-primary text-xs"
                        onClick={() => generateSummary.mutate(runDetail.id)}
                        disabled={generateSummary.isPending}
                      >
                        {generateSummary.isPending ? '⏳' : '✨'} Generate
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Agent outputs */}
                <div className="space-y-3">
                  {runDetail.agent_runs.map((agent) => (
                    <AgentCard key={agent.id} agent={agent} />
                  ))}
                </div>
                
                {/* Summary card */}
                <AnimatePresence>
                  {showSummary && summary && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4"
                    >
                      <SummaryCard summary={summary} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Right column - Position and info */}
        <div className="space-y-6">
          {/* Position card */}
          {question.position && (
            <div className="glass-card p-4 glow-green">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-mono text-sm text-signal-green">◈ Active Position</h3>
                <button
                  className="px-3 py-1.5 text-xs font-mono rounded bg-signal-blue/20 text-signal-blue border border-signal-blue/40 hover:bg-signal-blue/30 transition-colors disabled:opacity-50 font-semibold"
                  onClick={() => runReview.mutate()}
                  disabled={runReview.isPending}
                >
                  {runReview.isPending ? '⏳ Running...' : '🔍 Run Position Review'}
                </button>
              </div>

              {/* Last Review Status - Prominent banner */}
              {question.position.last_review && (
                <div className={clsx(
                  'mb-4 p-3 rounded-lg border',
                  question.position.last_review.action === 'hold' && 'bg-signal-blue/10 border-signal-blue/30',
                  question.position.last_review.action === 'add' && 'bg-signal-green/10 border-signal-green/30',
                  question.position.last_review.action === 'reduce' && 'bg-signal-amber/10 border-signal-amber/30',
                  question.position.last_review.action === 'exit' && 'bg-signal-red/10 border-signal-red/30'
                )}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={clsx(
                      'text-sm font-semibold',
                      question.position.last_review.action === 'hold' && 'text-signal-blue',
                      question.position.last_review.action === 'add' && 'text-signal-green',
                      question.position.last_review.action === 'reduce' && 'text-signal-amber',
                      question.position.last_review.action === 'exit' && 'text-signal-red'
                    )}>
                      {question.position.last_review.action === 'hold' && '✋ HOLD'}
                      {question.position.last_review.action === 'add' && '➕ ADD'}
                      {question.position.last_review.action === 'reduce' && '➖ REDUCE'}
                      {question.position.last_review.action === 'exit' && '🚪 EXIT'}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={clsx(
                        'text-[10px] px-1.5 py-0.5 rounded',
                        question.position.last_review.thesis_status === 'intact' && 'bg-signal-green/20 text-signal-green',
                        question.position.last_review.thesis_status === 'strengthened' && 'bg-signal-green/20 text-signal-green',
                        question.position.last_review.thesis_status === 'weakening' && 'bg-signal-amber/20 text-signal-amber',
                        question.position.last_review.thesis_status === 'broken' && 'bg-signal-red/20 text-signal-red'
                      )}>
                        Thesis: {question.position.last_review.thesis_status}
                      </span>
                      <span className="text-[10px] text-star-dim">
                        {formatTimeAgo(question.position.last_review.created_at)}
                      </span>
                    </div>
                  </div>
                  {question.position.last_review.thesis_assessment && (
                    <p className="text-xs text-star-muted line-clamp-2">
                      {question.position.last_review.thesis_assessment}
                    </p>
                  )}
                </div>
              )}
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="data-label">Total Qty</span>
                  <span className="data-value">{formatQuantity(question.position.yes_qty)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="data-label">Avg Entry</span>
                  <span className="data-value">
                    {question.position.avg_cost_yes !== undefined 
                      ? `${(question.position.avg_cost_yes * 100).toFixed(1)}¢`
                      : formatProbability(question.position.avg_cost)}
                  </span>
                </div>
                {question.position.cost_basis_usd !== undefined && (
                  <div className="flex justify-between">
                    <span className="data-label">Cost Basis</span>
                    <span className="data-value">${question.position.cost_basis_usd.toFixed(2)}</span>
                  </div>
                )}
                {question.position.unrealized_pnl !== undefined && (
                  <div className="flex justify-between">
                    <span className="data-label">Unrealized P&L</span>
                    <span className={clsx(
                      'data-value font-semibold',
                      (question.position.unrealized_pnl ?? 0) >= 0 ? 'text-signal-green' : 'text-signal-red'
                    )}>
                      {formatPnL(question.position.unrealized_pnl)}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Child positions breakdown */}
              {question.position.child_positions && question.position.child_positions.length > 0 && (
                <div className="mt-4 pt-4 border-t border-star-dim/20">
                  <span className="data-label text-[9px] sm:text-xs mb-2 block">Position Breakdown</span>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {question.position.child_positions.map((cp: any) => (
                      <div key={cp.question_id} className="flex flex-col xs:flex-row xs:justify-between text-[10px] sm:text-xs gap-1 xs:gap-2 p-2 bg-void-50 rounded">
                        <span
                          className="text-star truncate min-w-0"
                          title={cp.native_id || cp.title}
                        >
                          {cp.outcome_name || cp.title}
                        </span>
                        <span className="text-signal-green font-mono whitespace-nowrap shrink-0">
                          {formatQuantity(cp.yes_qty)} @ {(cp.avg_cost * 100).toFixed(1)}¢
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Review result */}
              {runReview.isSuccess && runReview.data && (
                <div className="mt-4 p-3 bg-signal-blue/10 border border-signal-blue/30 rounded-lg">
                  <div className="text-xs font-mono text-signal-blue mb-1">
                    Review Complete
                  </div>
                  <div className="text-sm text-star">
                    Action: <span className={clsx(
                      'font-semibold',
                      runReview.data.action === 'hold' && 'text-star-muted',
                      runReview.data.action === 'add' && 'text-signal-green',
                      runReview.data.action === 'reduce' && 'text-signal-yellow',
                      runReview.data.action === 'exit' && 'text-signal-red',
                    )}>{runReview.data.action?.toUpperCase()}</span>
                    {runReview.data.urgency && (
                      <span className="text-xs text-star-dim ml-2">({runReview.data.urgency})</span>
                    )}
                  </div>
                  {runReview.data.thesis_assessment && (
                    <p className="text-xs text-star-muted mt-2">{runReview.data.thesis_assessment}</p>
                  )}
                </div>
              )}
              
              {runReview.isError && (
                <div className="mt-4 p-3 bg-signal-red/10 border border-signal-red/30 rounded-lg">
                  <span className="text-xs text-signal-red">
                    Review failed: {(runReview.error as Error)?.message || 'Unknown error'}
                  </span>
                </div>
              )}
            </div>
          )}
          
          {/* Market info */}
          <div className="glass-card p-4">
            <h3 className="font-mono text-sm text-star-muted mb-4">Market Info</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="data-label">Bid</span>
                <span className="data-value">{formatProbability(question.market?.bid)}</span>
              </div>
              <div className="flex justify-between">
                <span className="data-label">Ask</span>
                <span className="data-value">{formatProbability(question.market?.ask)}</span>
              </div>
              <div className="flex justify-between">
                <span className="data-label">Spread</span>
                <span className="data-value">
                  {question.market?.spread_bps !== undefined 
                    ? `${(question.market.spread_bps / 100).toFixed(2)}%` 
                    : '—'}
                </span>
              </div>
              {question.market?.asof_ts && (
                <div className="pt-2 border-t border-star-dim/20 text-xs text-star-dim">
                  Updated: {formatTimeAgo(question.market.asof_ts)}
                </div>
              )}
            </div>
          </div>
          
          {/* Links */}
          <div className="glass-card p-4">
            <h3 className="font-mono text-sm text-star-muted mb-4">Links</h3>
            <div className="space-y-2">
              {question.url && (
                <a
                  href={question.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-signal-blue hover:underline truncate"
                >
                  🔗 View on Market
                </a>
              )}
              {question.native_id && (
                <p className="text-xs text-star-dim font-mono truncate">
                  ID: {question.native_id}
                </p>
              )}
            </div>
          </div>
          
          {/* Child markets comparison table */}
          {question.child_markets && question.child_markets.length > 0 && (
            <ChildMarketsTable childMarkets={question.child_markets} />
          )}
        </div>
      </div>
    </div>
  )
}


// Child Markets Comparison Table
function ChildMarketsTable({ childMarkets }: { childMarkets: any[] }) {
  // Sort by edge (highest first), then by market prob
  const sortedMarkets = [...childMarkets].sort((a, b) => {
    if (a.edge !== undefined && b.edge !== undefined) {
      return Math.abs(b.edge) - Math.abs(a.edge)
    }
    if (a.market_prob !== undefined && b.market_prob !== undefined) {
      return b.market_prob - a.market_prob
    }
    return 0
  })

  // Check if we have enhanced data
  const hasEnhancedData = sortedMarkets.some(m => m.market_prob !== undefined || m.our_prob !== undefined)

  if (!hasEnhancedData) {
    // Simple list view if no enhanced data
    return (
      <div className="glass-card p-3 sm:p-4">
        <h3 className="font-mono text-xs sm:text-sm text-star-muted mb-3 sm:mb-4">
          Child Markets ({childMarkets.length})
        </h3>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {sortedMarkets.map((child, i) => (
            <Link
              key={i}
              to={child.question_id ? `/question/${child.question_id}` : '#'}
              className="block p-2 bg-void-50 rounded text-[10px] sm:text-xs text-star-muted hover:text-star transition-colors truncate"
              title={child.outcome_name || child.title || child.slug}
            >
              {child.outcome_name || child.title || child.slug}
            </Link>
          ))}
        </div>
      </div>
    )
  }

  // Enhanced comparison table - use card layout on mobile, table on larger screens
  return (
    <div className="glass-card p-3 sm:p-4">
      <h3 className="font-mono text-xs sm:text-sm text-star-muted mb-3 sm:mb-4">
        Outcome Comparison ({childMarkets.length} markets)
      </h3>

      {/* Mobile card layout */}
      <div className="sm:hidden space-y-2 max-h-64 overflow-y-auto">
        {sortedMarkets.map((child, i) => (
          <Link
            key={i}
            to={child.question_id ? `/question/${child.question_id}` : '#'}
            className="block p-2 bg-void-50 rounded hover:bg-void-100 transition-colors"
          >
            <div className="text-[10px] text-star truncate mb-1 font-medium" title={child.outcome_name || child.title || child.slug}>
              {child.outcome_name || child.title || child.slug}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-[9px] font-mono">
              {child.market_prob !== undefined && (
                <span className="text-star-muted">Mkt: {formatProbability(child.market_prob)}</span>
              )}
              {child.our_prob !== undefined && (
                <span className="text-star">Ours: {formatProbability(child.our_prob)}</span>
              )}
              {child.edge !== undefined && (
                <span className={clsx('font-semibold', getEdgeClass(child.edge))}>
                  {formatEdge(child.edge)}
                </span>
              )}
              {child.liquidity !== undefined && (
                <span className="text-star-dim">${(child.liquidity / 1000).toFixed(0)}k</span>
              )}
            </div>
          </Link>
        ))}
      </div>

      {/* Desktop table layout */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-star-dim/20">
              <th className="text-left py-2 px-1 text-star-muted font-normal">Outcome</th>
              <th className="text-right py-2 px-1 text-star-muted font-normal">Market</th>
              <th className="text-right py-2 px-1 text-star-muted font-normal">Ours</th>
              <th className="text-right py-2 px-1 text-star-muted font-normal">Edge</th>
              <th className="text-right py-2 px-1 text-star-muted font-normal">TVL</th>
            </tr>
          </thead>
          <tbody>
            {sortedMarkets.map((child, i) => (
              <tr
                key={i}
                className="border-b border-star-dim/10 hover:bg-void-50/50"
              >
                <td className="py-2 px-1 max-w-[150px]">
                  <Link
                    to={child.question_id ? `/question/${child.question_id}` : '#'}
                    className="text-star hover:text-signal-blue transition-colors block truncate"
                    title={child.outcome_name || child.title || child.slug}
                  >
                    {child.outcome_name || child.title || child.slug}
                  </Link>
                </td>
                <td className="py-2 px-1 text-right font-mono text-star-muted whitespace-nowrap">
                  {child.market_prob !== undefined ? formatProbability(child.market_prob) : '—'}
                </td>
                <td className="py-2 px-1 text-right font-mono text-star whitespace-nowrap">
                  {child.our_prob !== undefined ? formatProbability(child.our_prob) : '—'}
                </td>
                <td className={clsx(
                  'py-2 px-1 text-right font-mono font-semibold whitespace-nowrap',
                  child.edge !== undefined && getEdgeClass(child.edge)
                )}>
                  {child.edge !== undefined ? formatEdge(child.edge) : '—'}
                </td>
                <td className="py-2 px-1 text-right font-mono text-star-dim whitespace-nowrap">
                  {child.liquidity !== undefined ? `$${(child.liquidity / 1000).toFixed(0)}k` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Decision Summary Card - Shows key trading decision factors at a glance
function DecisionSummaryCard({
  prob,
  edge,
  spread,
  liquidity,
  confidence,
  closeTs,
  position,
  hasRuns,
}: {
  prob?: number
  edge?: number
  spread?: number
  liquidity?: number
  confidence?: number
  closeTs?: string
  position?: any
  hasRuns: boolean
}) {
  const timeCountdown = formatTimeCountdown(closeTs)
  const opportunityScore = calculateOpportunityScore(edge, liquidity, confidence, spread)
  const zoneInfo = getProbabilityZone(prob)

  // Determine trade recommendation with zone-aware logic
  const getRecommendation = () => {
    if (!hasRuns) {
      return { text: 'Run prediction first', color: 'text-star-muted', action: 'analyze' }
    }
    if (edge === undefined) {
      return { text: 'No edge calculated', color: 'text-star-muted', action: 'none' }
    }

    // Check zone-based restrictions
    if (zoneInfo) {
      const edgeBps = Math.abs(edge) * 10000

      // Record-only zones (extreme probabilities)
      if (zoneInfo.mode === 'record_only') {
        return {
          text: 'Recording for calibration (no trade)',
          color: 'text-signal-red',
          action: 'record',
        }
      }

      // Check if edge meets zone minimum
      if (edgeBps < zoneInfo.minEdgeBps) {
        return {
          text: `Edge below ${zoneInfo.minEdgeBps}bps threshold for ${zoneInfo.label}`,
          color: 'text-star-muted',
          action: 'wait',
        }
      }

      // Small-only zones
      if (zoneInfo.mode === 'small_only') {
        if (edge > 0) {
          return {
            text: `Small position only (${(zoneInfo.maxSizeFactor * 100).toFixed(0)}% max)`,
            color: 'text-signal-amber',
            action: 'small_buy',
          }
        }
        return {
          text: 'High variance zone - monitor only',
          color: 'text-signal-amber',
          action: 'monitor',
        }
      }
    }

    if (position) {
      // Already have position
      if (Math.abs(edge) < 0.02) {
        return { text: 'Edge diminished - review position', color: 'text-signal-amber', action: 'review' }
      }
      if (edge > 0.05) {
        return { text: 'Strong edge - consider adding', color: 'text-signal-green', action: 'add' }
      }
      return { text: 'Hold current position', color: 'text-signal-blue', action: 'hold' }
    }
    // No position yet
    if (edge > 0.05) {
      return { text: 'Strong buy signal', color: 'text-signal-green', action: 'buy' }
    }
    if (edge < -0.05) {
      return { text: 'Strong sell signal', color: 'text-signal-red', action: 'sell' }
    }
    if (Math.abs(edge) > 0.02) {
      return { text: 'Moderate opportunity', color: 'text-signal-blue', action: 'consider' }
    }
    return { text: 'No significant edge', color: 'text-star-muted', action: 'wait' }
  }

  const recommendation = getRecommendation()

  return (
    <div className="glass-card p-3 sm:p-4 mb-4 sm:mb-6">
      {/* Mobile: Stack vertically, Desktop: Flex row */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        {/* Main recommendation */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
            <span className={clsx('text-sm sm:text-lg font-semibold', recommendation.color)}>
              {recommendation.action === 'buy' && '📈'}
              {recommendation.action === 'sell' && '📉'}
              {recommendation.action === 'hold' && '✋'}
              {recommendation.action === 'add' && '➕'}
              {recommendation.action === 'review' && '🔍'}
              {recommendation.action === 'analyze' && '📊'}
              {recommendation.action === 'consider' && '🤔'}
              {recommendation.action === 'wait' && '⏸️'}
              {recommendation.action === 'none' && '—'}
              {recommendation.action === 'record' && '📝'}
              {recommendation.action === 'small_buy' && '📈'}
              {recommendation.action === 'monitor' && '👁️'}
              {' '}
              <span className="break-words">{recommendation.text}</span>
            </span>
            {/* Zone badge */}
            {zoneInfo && (
              <span className={clsx(
                'text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded border font-mono shrink-0',
                getZoneBadgeClass(zoneInfo.mode)
              )}>
                {zoneInfo.emoji} {zoneInfo.label}
              </span>
            )}
          </div>
          {/* Key factors row */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-[10px] sm:text-xs font-mono">
            {/* Edge */}
            {edge !== undefined && (
              <div className="flex items-center gap-1">
                <span className="text-star-muted">Edge:</span>
                <span className={clsx('font-semibold', getEdgeClass(edge))}>
                  {formatEdgeBps(edge)}
                </span>
                {zoneInfo && (
                  <span className="text-star-dim hidden xs:inline">
                    (min {zoneInfo.minEdgeBps}bps)
                  </span>
                )}
              </div>
            )}
            {/* Max Size Factor */}
            {zoneInfo && zoneInfo.mode !== 'trade' && (
              <div className="flex items-center gap-1">
                <span className="text-star-muted">Max:</span>
                <span className={clsx('font-semibold', getZoneClass(zoneInfo.zone))}>
                  {(zoneInfo.maxSizeFactor * 100).toFixed(0)}%
                </span>
              </div>
            )}
            {/* Spread */}
            {spread !== undefined && (
              <div className="flex items-center gap-1">
                <span className="text-star-muted">Spread:</span>
                <span className={getSpreadClass(spread)}>
                  {formatSpread(spread)}
                </span>
              </div>
            )}
            {/* Liquidity - hide on mobile */}
            {liquidity !== undefined && (
              <div className="hidden xs:flex items-center gap-1">
                <span className="text-star-muted">TVL:</span>
                <span className="text-star">
                  ${(liquidity / 1000).toFixed(0)}k
                </span>
              </div>
            )}
            {/* Opportunity score */}
            {opportunityScore !== null && (
              <div className="flex items-center gap-1">
                <span className="text-star-muted">Score:</span>
                <span className={clsx('font-semibold', getOpportunityScoreClass(opportunityScore))}>
                  {opportunityScore}/100
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Time countdown + Position status - side by side on mobile */}
        <div className="flex items-start gap-3 sm:gap-4 shrink-0">
          {/* Time countdown */}
          <div className="text-left sm:text-right">
            <span className="text-[10px] sm:text-xs text-star-muted block mb-1">Time Left</span>
            <span className={clsx('text-sm sm:text-lg font-mono font-semibold', getTimeCountdownClass(timeCountdown.urgency))}>
              {timeCountdown.text}
              {timeCountdown.urgency === 'critical' && ' ⚠️'}
            </span>
          </div>

          {/* Position status */}
          {position && (
            <div className="text-left sm:text-right pl-3 sm:pl-4 border-l border-star-dim/20">
              <span className="text-[10px] sm:text-xs text-signal-green block mb-1">Position</span>
              <span className="text-sm sm:text-lg font-mono font-semibold text-signal-green">
                {formatPnL(position.unrealized_pnl)}
              </span>
              {position.last_review && (
                <div className="mt-1">
                  <span className={clsx('text-[9px] sm:text-[10px]', getActionBadgeClass(position.last_review.action))}>
                    {position.last_review.action.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Agent Card Component with expandable sources and full response
function AgentCard({ agent }: { agent: any }) {
  const [expanded, setExpanded] = useState(false)
  const [showSources, setShowSources] = useState(false)
  const [showFullResponse, setShowFullResponse] = useState(false)

  return (
    <div className="p-2 sm:p-3 bg-void-50 rounded-lg">
      {/* Header row - stack on mobile */}
      <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-2 mb-2">
        <span className="font-mono text-xs sm:text-sm text-star flex items-center gap-2 truncate">
          <span className="shrink-0">{getAgentIcon(agent.agent_name)}</span>
          <span className="truncate">{agent.agent_name}</span>
        </span>
        <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs font-mono flex-wrap">
          {/* Only show probability for synthesis - all others return directional data */}
          {agent.agent_name === 'synthesis' &&
           agent.prob !== undefined && agent.prob !== null && (
            <span className="text-star-muted whitespace-nowrap">
              P: {formatProbability(agent.prob)}
            </span>
          )}
          {agent.agent_name === 'synthesis' &&
           agent.confidence !== undefined && agent.confidence !== null && agent.confidence > 0 && (
            <span className="text-star-muted whitespace-nowrap hidden xs:inline">
              C: {(agent.confidence * 100).toFixed(0)}%
            </span>
          )}
          {agent.evidence_count > 0 && (
            <button
              onClick={() => setShowSources(!showSources)}
              className={clsx(
                'px-1.5 sm:px-2 py-0.5 rounded border transition-colors whitespace-nowrap',
                showSources
                  ? 'bg-signal-blue/20 border-signal-blue/50 text-signal-blue'
                  : 'bg-signal-blue/10 border-signal-blue/30 text-signal-blue hover:bg-signal-blue/20'
              )}
            >
              📎 {agent.evidence_count}
            </button>
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-star-dim hover:text-star transition-colors p-1"
          >
            {expanded ? '▼' : '▶'}
          </button>
        </div>
      </div>
      
      {/* Summary */}
      {agent.summary && (
        <p className={clsx(
          'text-xs text-star-muted',
          !expanded && 'line-clamp-2'
        )}>
          {agent.summary}
        </p>
      )}

      {/* Key insights preview - always visible */}
      {agent.output_json?.key_factors && Array.isArray(agent.output_json.key_factors) && agent.output_json.key_factors.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {agent.output_json.key_factors.slice(0, 3).map((factor: string, i: number) => (
            <span
              key={i}
              className="text-[10px] px-1.5 py-0.5 rounded bg-signal-blue/10 text-signal-blue border border-signal-blue/20"
              title={factor}
            >
              {factor.length > 40 ? factor.substring(0, 40) + '...' : factor}
            </span>
          ))}
          {agent.output_json.key_factors.length > 3 && (
            <span className="text-[10px] text-star-dim">
              +{agent.output_json.key_factors.length - 3} more
            </span>
          )}
        </div>
      )}

      {/* Direction indicator - use direction_assessment for research agents, prob for synthesis */}
      {(() => {
        // For synthesis only, derive direction from probability
        if (agent.agent_name === 'synthesis') {
          if (agent.prob === undefined || agent.prob === null) return null
          const direction = agent.prob > 0.6 ? 'bullish' : agent.prob < 0.4 ? 'bearish' : 'neutral'
          return (
            <div className="mt-2 flex items-center gap-2">
              <span className={clsx(
                'text-[10px] font-mono px-1.5 py-0.5 rounded',
                direction === 'bullish' ? 'bg-signal-green/10 text-signal-green border border-signal-green/20' :
                direction === 'bearish' ? 'bg-signal-red/10 text-signal-red border border-signal-red/20' :
                'bg-star-dim/10 text-star-muted border border-star-dim/20'
              )}>
                {direction === 'bullish' ? '↑ Bullish' : direction === 'bearish' ? '↓ Bearish' : '→ Neutral'}
              </span>
            </div>
          )
        }
        // For all research agents (news, quant, skeptic, baserate), use direction_assessment from output
        const directionData = agent.output_json?.direction_assessment
        if (!directionData?.direction) return null
        const direction = directionData.direction.toLowerCase()
        return (
          <div className="mt-2 flex items-center gap-2">
            <span className={clsx(
              'text-[10px] font-mono px-1.5 py-0.5 rounded',
              direction === 'bullish' ? 'bg-signal-green/10 text-signal-green border border-signal-green/20' :
              direction === 'bearish' ? 'bg-signal-red/10 text-signal-red border border-signal-red/20' :
              'bg-star-dim/10 text-star-muted border border-star-dim/20'
            )}>
              {direction === 'bullish' ? '↑ Bullish' : direction === 'bearish' ? '↓ Bearish' : '→ Neutral'}
            </span>
            {directionData.direction_strength && (
              <span className="text-[10px] text-star-dim">
                ({directionData.direction_strength})
              </span>
            )}
          </div>
        )
      })()}

      {/* Expanded content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3 space-y-3"
          >
            {/* Full response toggle */}
            {agent.output_json && (
              <div>
                <button
                  onClick={() => setShowFullResponse(!showFullResponse)}
                  className="text-xs text-signal-blue hover:underline"
                >
                  {showFullResponse ? '▼ Hide' : '▶ Show'} Full Agent Response
                </button>
                
                {showFullResponse && (
                  <pre className="mt-2 p-2 bg-void rounded text-xs text-star-dim overflow-x-auto max-h-64">
                    {JSON.stringify(agent.output_json, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Sources panel */}
      <AnimatePresence>
        {showSources && agent.evidence && agent.evidence.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-star-dim/20"
          >
            <h4 className="text-[10px] sm:text-xs font-mono text-star-muted mb-2">Sources</h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {agent.evidence.map((ev: any) => (
                <div key={ev.id} className="p-2 bg-void rounded text-[10px] sm:text-xs overflow-hidden">
                  <div className="flex flex-col xs:flex-row xs:items-start xs:justify-between gap-1 xs:gap-2 mb-1">
                    {ev.source_url ? (
                      <a
                        href={ev.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-signal-blue hover:underline font-medium truncate min-w-0 block"
                        title={ev.source_url}
                      >
                        {ev.title || new URL(ev.source_url).hostname}
                      </a>
                    ) : (
                      <span className="text-star font-medium truncate min-w-0">
                        {ev.title || 'Source'}
                      </span>
                    )}
                    {ev.source_type && (
                      <span className="text-star-dim text-[9px] sm:text-[10px] shrink-0 badge-muted">
                        {ev.source_type}
                      </span>
                    )}
                  </div>
                  {ev.snippet && (
                    <p className="text-star-dim line-clamp-2 break-words">
                      {ev.snippet}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
